import { Effect, Option, Result, Schema, SchemaGetter, SchemaIssue } from "effect";

export const WorkspaceLimits = /* @__PURE__ */ (() => Schema.Struct({ files: Schema.Int, fileBytes: Schema.Int, totalBytes: Schema.Int }))();
export type WorkspaceLimits = typeof WorkspaceLimits.Type;
export const WorkspaceFiles = /* @__PURE__ */ (() => Schema.Record(Schema.String, Schema.mutableKey(Schema.String)))();
export const WorkspaceUpdate = /* @__PURE__ */ (() => Schema.Struct({
  files: WorkspaceFiles,
  openTabs: Schema.Array(Schema.String).pipe(Schema.mutable),
  currentFile: Schema.optional(Schema.String),
  folders: Schema.Array(Schema.String).pipe(Schema.mutable),
  options: Schema.Struct({ bundle: Schema.Boolean, minify: Schema.Boolean })
}))();
export type WorkspaceUpdate = typeof WorkspaceUpdate.Type;
const Fields = /* @__PURE__ */ (() => Schema.Struct({ files: Schema.optionalKey(Schema.Unknown), openTabs: Schema.optionalKey(Schema.Unknown), currentFile: Schema.optionalKey(Schema.Unknown), folders: Schema.optionalKey(Schema.Unknown), options: Schema.optionalKey(Schema.Unknown) }))();
const Values = /* @__PURE__ */ (() => Schema.Array(Schema.Unknown))();
const Options = /* @__PURE__ */ (() => Schema.Struct({ bundle: Schema.optionalKey(Schema.Unknown), minify: Schema.optionalKey(Schema.Unknown) }))();
const FileValues = /* @__PURE__ */ (() => Schema.Record(Schema.String, Schema.Unknown))();
const Folder = /* @__PURE__ */ (() => Schema.String.check(Schema.makeFilter(value => value.length > 0 && value.length <= 512 && !value.split("/").some(part => part === "" || part === ".."))))();
const Path = /* @__PURE__ */ (() => Schema.String.check(Schema.makeFilter(value => Boolean(value.trim()) && value.length <= 512)))();
const issue = (message: string) => new SchemaIssue.InvalidValue({ message });
const formatted = SchemaIssue.makeFormatterStandardSchemaV1();
export const workspaceInputMessage = (error: Schema.SchemaError, fallback = "Invalid request body.") => formatted(error.issue).issues[0]?.message ?? fallback;

/** Source execution limits retain insertion order and UTF-8 byte accounting. */
export const workspaceSourceFiles = (limits: WorkspaceLimits) => FileValues.check(Schema.makeFilter(files => {
  const entries = Object.entries(files);
  if (entries.length > limits.files) return `At most ${limits.files} files are supported.`;
  const encoder = new TextEncoder();
  let total = 0;
  for (const [path, contents] of entries) {
    if (!Schema.is(Schema.String)(contents)) return `File ${path} must be a string.`;
    const size = encoder.encode(contents).byteLength;
    if (size > limits.fileBytes) return `File ${path} exceeds ${limits.fileBytes} bytes.`;
    total += size;
  }
  return total > limits.totalBytes ? "Workspace exceeds the total size limit." : true;
})).pipe(Schema.decodeTo(WorkspaceFiles));

/** Historical editor normalization, without canonicalizing or rewriting source. */
export const workspaceInput = (limits: WorkspaceLimits) => Schema.Unknown.pipe(Schema.decodeTo(WorkspaceUpdate, {
  decode: SchemaGetter.transformOrFail(value => Effect.gen(function* () {
    if (!Schema.is(Schema.ObjectKeyword)(value)) return yield* Effect.fail(issue("Invalid request body."));
    const fields = Option.getOrElse(Schema.decodeUnknownOption(Fields)(value), (): typeof Fields.Type => ({}));
    if (!Schema.is(FileValues)(fields.files)) return yield* Effect.fail(issue("files must be an object."));
    if (Object.keys(fields.files).length === 0) return yield* Effect.fail(issue("At least one file is required."));
    const files = yield* Schema.decodeUnknownEffect(workspaceSourceFiles(limits))(fields.files).pipe(Effect.mapError(error => error.issue));
    const paths = Object.keys(files);
    if (!paths.every(Schema.is(Path))) return yield* Effect.fail(issue("Invalid file path."));
    const tabs = Option.getOrElse(Schema.decodeUnknownOption(Values)(fields.openTabs), (): typeof Values.Type => []);
    const folders = Option.getOrElse(Schema.decodeUnknownOption(Values)(fields.folders), (): typeof Values.Type => []);
    const options = Option.getOrElse(Schema.decodeUnknownOption(Options)(fields.options), (): typeof Options.Type => ({}));
    return {
      files, openTabs: tabs.filter(Schema.is(Schema.String)).filter(path => paths.includes(path)),
      currentFile: Schema.is(Schema.String)(fields.currentFile) && paths.includes(fields.currentFile) ? fields.currentFile : undefined,
      options: { bundle: Schema.is(Schema.Boolean)(options.bundle) ? options.bundle : true, minify: Schema.is(Schema.Boolean)(options.minify) ? options.minify : true },
      folders: [...new Set(folders.filter(Schema.is(Schema.String)).map(path => path.replace(/^\/+|\/+$/g, "").trim()).filter(Schema.is(Folder)))].slice(0, 128)
    };
  })),
  encode: SchemaGetter.transform(value => value)
}));
export const workspaceSourceLimitMessage = (limits: WorkspaceLimits, files: Readonly<Record<string, unknown>>) => Result.match(
  Schema.decodeUnknownResult(workspaceSourceFiles(limits))(files), { onFailure: workspaceInputMessage, onSuccess: () => undefined }
);
